import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Button, EmptyState, Panel, PanelHeader, RailLabel, Skeleton, Stamp, type Tone } from '@/components/ui';
import { backend, type CapabilityState } from '@/api/backend';
import { GapReport } from '@/components/GapReport';
import { CollectorApproval } from '@/components/CollectorApproval';
import { say, BLOCKER, CAPABILITY, CAPABILITY_ORDER, type Blocker } from '@/lib/vocabulary';

const stateTone: Record<CapabilityState, Tone> = {
  LIVE_VERIFIED: 'ok',
  CONFIGURED: 'warn',
  COMPILED: 'mut',
  UNAVAILABLE: 'mut',
};

/** Deterministic case transform only: the exact code is preserved on the row. */
function humanizeGap(code: string): string {
  const t = say(code);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * System capabilities, rendered literally. COMPILED and CONFIGURED are never
 * promoted to live support; knownGaps are product facts shown prominently.
 */
export function Operations() {
  // The gap probes need a real bucket and company to ask about, so they use
  // the first the operator can see rather than an invented id.
  const buckets = useQuery({
    queryKey: ['live-buckets'],
    queryFn: ({ signal }) => backend.listAllBuckets(signal),
    staleTime: 30_000,
    retry: false,
  });
  const probeBucketId = buckets.data?.items[0]?.bucket.id ?? null;
  const probeCompanyId = buckets.data?.items[0]?.bucket.companyId ?? null;

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['capabilities'],
    queryFn: ({ signal }) => api.getCapabilities(signal),
    staleTime: 60_000,
  });

  if (isPending)
    return (
      <div className="space-y-2 p-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );

  if (isError || !data)
    return (
      <EmptyState
        title="Capabilities unavailable"
        description="The API did not answer the capability probe. No support level is assumed."
        action={<Button variant="ghost" onClick={() => refetch()}>Retry</Button>}
      />
    );

  return (
    <div className="min-h-0 flex-1 animate-pop space-y-3 overflow-y-auto p-5">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-h2 text-ink">System capabilities</h1>
        <Stamp tone={data.runtimeEnvironment === 'PRODUCTION' ? 'ok' : 'mut'}>{say(data.runtimeEnvironment)}</Stamp>
        <span className="mono text-caption text-ink-3">api {data.apiVersion}</span>
        <a
          href="https://api.poseidon.withmeridian.ai/reference"
          target="_blank"
          rel="noreferrer"
          className="ml-auto rounded border border-line bg-panel-2 px-2.5 py-1 text-small text-acc-2 transition-colors duration-fast hover:bg-hov"
        >
          API reference ↗
        </a>
      </div>

      {(() => {
        const all = Object.entries(data.capabilities);
        const live = all.filter(([, v]) => v === 'IMPLEMENTED');
        const not = all.filter(([, v]) => v !== 'IMPLEMENTED');
        const row = ([k]: [string, unknown], on: boolean) => {
          const c = CAPABILITY[k] ?? { name: k, does: '' };
          return (
            <div key={k} className="flex flex-wrap items-baseline gap-x-3 border-b border-line px-4 py-2 text-small last:border-0">
              <span className={on ? 'text-ink' : 'text-ink-3'}>{c.name}</span>
              <span className="min-w-0 flex-1 text-caption text-ink-4">{c.does}</span>
            </div>
          );
        };
        return (
          <>
            <Panel className="mb-3">
              <PanelHeader
                title={`What you can do today · ${live.length} of ${all.length}`}
                sub="each of these is working against live data right now"
              />
              {live.map((e) => row(e, true))}
            </Panel>
            <Panel className="mb-3">
              <PanelHeader
                title="What is not here yet, and why"
                sub="one chain · everything below keys off knowing which tool made a file"
              />
              <ol className="border-b border-line px-4 py-3">
                {CAPABILITY_ORDER.map((stepText, i) => (
                  <li key={stepText} className="flex gap-3 py-1 text-small">
                    <span className="mono flex-none text-caption text-acc-2">{i + 1}</span>
                    <span className={i === 0 ? 'text-ink' : 'text-ink-3'}>
                      {stepText}
                      {i === 0 && <span className="ml-2 text-caption text-ok">working today</span>}
                    </span>
                  </li>
                ))}
              </ol>
              {(['RECONSTRUCTION', 'PROMOTION', 'EXECUTOR', 'NONE'] as Blocker[]).map((b) => {
                const mine = not.filter(([k]) => (CAPABILITY[k]?.waitsOn ?? 'RECONSTRUCTION') === b);
                if (mine.length === 0) return null;
                return (
                  <div key={b} className="border-b border-line last:border-0">
                    <div className="bg-panel-2 px-4 py-2">
                      <span className="text-caption font-semibold uppercase tracking-wider text-ink-3">
                        {BLOCKER[b].title} · {mine.length}
                      </span>
                      <p className="mt-0.5 text-caption text-ink-4">{BLOCKER[b].why}</p>
                    </div>
                    {mine.map((e) => row(e, false))}
                  </div>
                );
              })}
            </Panel>
          </>
        );
      })()}

      <Panel className="mb-3">
        <PanelHeader title="Derived retention" sub="per-class retention for Poseidon-owned derived artifacts" />
        <div className="p-3">
          <div className="mb-2 flex items-center gap-2">
            <Stamp tone={data.derivedRetention.state === 'IMPLEMENTED' ? 'ok' : 'mut'}>{say(data.derivedRetention.state)}</Stamp>
            <span className="text-small text-ink-2">{data.derivedRetention.basis}</span>
          </div>
          {data.derivedRetention.policies.map((pcy) => (
            <div key={pcy.artifactClass} className="flex items-baseline justify-between border-b border-line py-1 text-small last:border-0">
              <span className="mono text-ink-2">{pcy.artifactClass}</span>
              <span className="mono text-small text-ink">{pcy.retentionSeconds}s</span>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel>
          <PanelHeader title="Components" sub="LIVE_VERIFIED is the only liveness claim" />
          <div className="p-2">
            {data.components.map((c) => (
              <div key={c.capability} className="flex items-start gap-3 border-b border-line px-2 py-2 last:border-0">
                <Stamp tone={stateTone[c.state]} className="mt-0.5 flex-none">
                  {c.state}
                </Stamp>
                <div className="min-w-0">
                  <div className="mono break-all text-row text-ink">{c.capability}</div>
                  <div className="text-small text-ink-3">{c.basis}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <div className="flex flex-col gap-3">
          <Panel>
            <PanelHeader title="Tool adapters" sub="support levels, verbatim" />
            <div className="p-2">
              {data.tools.map((t) => (
                <div key={t.tool + t.capability} className="flex items-center gap-3 border-b border-line px-2 py-2 last:border-0">
                  <span className="mono w-40 flex-none text-row text-ink">{t.tool}</span>
                  <span className="mono min-w-0 flex-1 text-small text-ink-3">{t.capability}</span>
                  <Stamp tone={t.supportLevel === 'PRODUCTION' ? 'ok' : t.supportLevel === 'UNSUPPORTED' ? 'mut' : 'warn'}>
                    {t.supportLevel}
                  </Stamp>
                </div>
              ))}
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Known gaps" sub="what an operator cannot do yet, and why" />
            <div className="p-3">
              <RailLabel className="px-0" count={data.knownGaps.length}>
                Disclosed by the API
              </RailLabel>
              {data.knownGaps.map((g) => (
                <div key={g} title={g} className="border-b border-line py-2 last:border-0">
                  <span className="block text-row text-ink-2">{humanizeGap(g)}</span>
                  <span className="mono block break-all text-caption text-ink-4">{g}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
      {/* Checked against the API on every visit, so a gap that closes
          turns green here without anyone editing a list. */}
      <CollectorApproval />
      <GapReport bucketId={probeBucketId} companyId={probeCompanyId} />
    </div>
  );
}
